import React, { useContext, useEffect, useState } from 'react'
import { Alert, Box, Grid, Skeleton, Stack } from '@mui/material'
import { motion } from 'framer-motion'
import GroupsIcon from '@mui/icons-material/Groups'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import SchoolIcon from '@mui/icons-material/School'
import RateReviewIcon from '@mui/icons-material/RateReview'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import { TenantSettingsContext } from '../../../../App'
import API from '../../../../services/API'
import StatTile from '../../../userPages/dashboard/managerOverview/StatTile'
import { viz } from '../../../userPages/dashboard/managerOverview/overviewTheme'
import WorkforceHero from './WorkforceHero'
import CompliancePulse from './CompliancePulse'
import {
  CourseComplianceChart,
  RoleMixChart,
  StartersTrendChart,
} from './WorkforceCharts'
import ActionCentre, { buildActionItems } from './ActionCentre'
import RadiationDoseOverview from './RadiationDoseOverview'

const sectionMotion = (index) => ({
  component: motion.div,
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, delay: 0.08 * index, ease: 'easeOut' },
})

const EmployeeDashboard = () => {
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [summary, setSummary] = useState(null)
  const [error, setError] = useState(false)
  const [training, setTraining] = useState(null)
  const [lists, setLists] = useState(null)
  const [doseOperators, setDoseOperators] = useState([])

  useEffect(() => {
    API.get('/employee-dashboard')
      .then((res) => {
        if (res.status === 200) setSummary(res.data)
      })
      .catch((e) => {
        console.error(e)
        setError(true)
      })

    API.get('/employee-dashboard/mandatory-training-compliance')
      .then((res) => {
        if (res.status === 200) setTraining(res.data)
      })
      .catch((e) => console.error(e))

    // Degrades to a hidden section when imaging isn't in use or the fetch
    // fails
    API.get('/imaging-doses/overview')
      .then((res) => {
        if (res.status === 200) setDoseOperators(res.data.operators ?? [])
      })
      .catch((e) => console.error(e))

    // Each worklist degrades independently — a failed fetch (including a 403
    // on the reviewer-only appraisals list) hides that category rather than
    // taking the page down
    const grab = (url) =>
      API.get(url)
        .then((res) => res.data)
        .catch((e) => {
          console.error(e)
          return null
        })

    Promise.all([
      grab('/employees-expiredmandatorytraining'),
      grab('/employees-expireddbslist'),
      grab('/employees-expired-driving-licences'),
      grab('/employees-expired-driving-licence-checks'),
      grab('/employees-without-id-cards'),
      grab('/staff-with-pending-mandatory-training'),
      grab('/appraisals/pending-review'),
      grab('/employees-outstanding-occupational-health'),
    ]).then(
      ([
        expiredTraining,
        dbs,
        licences,
        licenceChecks,
        idCards,
        trainingReviews,
        appraisals,
        occupationalHealth,
      ]) =>
        setLists({
          expiredTraining,
          dbs,
          licences,
          licenceChecks,
          idCards,
          trainingReviews,
          appraisals,
          occupationalHealth,
        }),
    )
  }, [])

  if (error) {
    return (
      <Alert severity="error">
        We couldn't load the workforce dashboard. Please try again shortly.
      </Alert>
    )
  }

  if (!summary) {
    return (
      <Stack gap={2}>
        <Skeleton variant="rounded" height={220} />
        <Grid container spacing={2}>
          {[...Array(6)].map((_, i) => (
            <Grid item xs={6} sm={4} lg={2} key={i}>
              <Skeleton variant="rounded" height={96} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Skeleton variant="rounded" height={300} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <Skeleton variant="rounded" height={300} />
          </Grid>
        </Grid>
      </Stack>
    )
  }

  const actionItems = buildActionItems(lists)
  const actionsTotal = actionItems.reduce(
    (total, item) => total + item.count,
    0,
  )
  const countFor = (key) =>
    actionItems.find((item) => item.key === key)?.count ?? 0
  const appraisalsWaitingForStaff = (lists?.appraisals ?? []).filter(
    (appraisal) => appraisal.status === 'Awaiting Employee',
  ).length
  // Only reviewer-actionable appraisals count as pending reviews
  const pendingReviews =
    countFor('training-reviews') +
    (countFor('appraisals') - appraisalsWaitingForStaff)

  return (
    <Stack gap={2.5} pb={4}>
      <WorkforceHero
        orgSettings={orgSettings}
        summary={summary}
        pendingReviews={pendingReviews}
        actionsTotal={actionsTotal}
      />

      <Box {...sectionMotion(1)}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              icon={GroupsIcon}
              label="Active staff"
              value={summary.activeStaffCount}
              hint={`+ ${summary.newStarters ?? 0} new starter${
                summary.newStarters === 1 ? '' : 's'
              }`}
              url="/employees"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              icon={PersonAddIcon}
              label="New applicants"
              value={summary.applicants ?? 0}
              hint="Awaiting a decision"
              accent={(summary.applicants ?? 0) > 0 ? '#268EBB' : viz.good}
              url="/employees/applications"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              icon={GppMaybeIcon}
              label="DBS out of date"
              value={summary.expiredDBS ?? 0}
              hint="Need a new check"
              accent={(summary.expiredDBS ?? 0) > 0 ? viz.critical : viz.good}
              url="/dbs-checks"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              icon={SchoolIcon}
              label="Training uploads"
              value={countFor('training-reviews')}
              hint="Certificates to approve"
              accent={countFor('training-reviews') > 0 ? viz.warning : viz.good}
              url="/pending-mandatory-training"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              icon={RateReviewIcon}
              label="Appraisals"
              value={countFor('appraisals')}
              hint={
                appraisalsWaitingForStaff > 0
                  ? `${appraisalsWaitingForStaff} waiting for staff`
                  : 'Awaiting HR review'
              }
              accent={countFor('appraisals') > 0 ? viz.warning : viz.good}
              url="/appraisals/pending-review"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              icon={PlaylistAddCheckIcon}
              label="Open actions"
              value={actionsTotal}
              hint="Across all areas"
              accent={actionsTotal > 0 ? viz.warning : viz.good}
            />
          </Grid>
        </Grid>
      </Box>

      <Box {...sectionMotion(2)}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Stack gap={2}>
              <CompliancePulse summary={summary} training={training} />
              <CourseComplianceChart
                courses={training?.complianceCounts ?? []}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <RoleMixChart headcountByRole={summary.headcountByRole} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <StartersTrendChart
                    startersByMonth={summary.startersByMonth}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Grid>
          <Grid item xs={12} lg={4}>
            <ActionCentre items={actionItems} loading={!lists} />
          </Grid>
        </Grid>
      </Box>

      {doseOperators.length > 0 && (
        <Box {...sectionMotion(3)}>
          <RadiationDoseOverview operators={doseOperators} />
        </Box>
      )}
    </Stack>
  )
}

export default EmployeeDashboard
